/**
 * Control to allow selecting between models and weave product quickstarts.
 */

import {
  MOON_50,
  MOON_250,
  MOON_500,
  TEAL_300,
  TEAL_600,
} from '@wandb/weave/common/css/color.styles';
import {hexToRGB} from '@wandb/weave/common/css/globals.styles';
import {
  IconCheckmark,
  IconModel,
  IconWeave,
} from '@wandb/weave/components/Icon';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

export enum Product {
  Models = 'models',
  Weave = 'weave',
}

type QuickStartProductSelectProps = {
  selected: Product;
  onSelectProduct: (product: Product) => void;
};

const ProductSelectContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
`;
ProductSelectContainer.displayName = 'S.ProductSelectContainer';

// TODO: One-off "teal-200" color being used for background. Follow up with design.
const ProductBase = styled.div<{
  $isSelected: boolean;
}>`
  flex: 1 1 50%;
  padding: 16px;
  border-style: solid;
  border-color: ${props => (props.$isSelected ? TEAL_300 : MOON_250)};
  background-color: ${props => (props.$isSelected ? '#E1F7FA' : 'transparent')};
  cursor: ${props => (props.$isSelected ? 'default' : 'pointer')};
  display: flex;
  gap: 10px;
  align-items: center;
`;
ProductBase.displayName = 'S.ProductBase';

const ProductModels = styled(ProductBase)`
  border-width: 1px;
  border-radius: 8px 0px 0px 8px;
  border-right-color: ${TEAL_300};
`;
ProductModels.displayName = 'S.ProductModels';

const ProductWeave = styled(ProductBase)`
  border-width: 1px 1px 1px 0px;
  border-radius: 0px 8px 8px 0px;
`;
ProductWeave.displayName = 'S.ProductWeave';

const Circle = styled.div<{
  $isSelected: boolean;
}>`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props =>
    props.$isSelected ? hexToRGB(TEAL_300, 0.48) : MOON_50};
  color: ${props => (props.$isSelected ? TEAL_600 : MOON_500)};
`;
Circle.displayName = 'S.Circle';

const Text = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;
Text.displayName = 'S.Text';

const Title = styled.div`
  font-family: Source Sans Pro;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
`;
Title.displayName = 'S.Title';

const Description = styled.div`
  font-family: Source Sans Pro;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
`;
Description.displayName = 'S.Description';

const Checkmark = styled(IconCheckmark)`
  width: 32px;
  height: 32px;
  color: ${TEAL_600};
`;
Checkmark.displayName = 'S.Checkmark';

export const QuickStartProductSelect = ({
  selected,
  onSelectProduct,
}: QuickStartProductSelectProps) => {
  const isModelsSelected = selected === Product.Models;
  return (
    <ProductSelectContainer>
      <ProductModels
        $isSelected={isModelsSelected}
        onClick={() => onSelectProduct(Product.Models)}>
        <Circle $isSelected={isModelsSelected}>
          <IconModel />
        </Circle>
        <Text>
          <Title>W&B Models</Title>
          <Description>Build & fine-tune models</Description>
        </Text>
        {isModelsSelected && <Checkmark />}
      </ProductModels>
      <ProductWeave
        $isSelected={!isModelsSelected}
        onClick={() => onSelectProduct(Product.Weave)}>
        <Circle $isSelected={!isModelsSelected}>
          <IconWeave />
        </Circle>
        <Text>
          <Title>W&B Weave</Title>
          <Description>Develop AI apps</Description>
        </Text>
        {!isModelsSelected && <Checkmark />}
      </ProductWeave>
    </ProductSelectContainer>
  );
};
